import type { useToast } from "primevue/usetoast";

/**
 * This Plugin handles all errors which are not already handled in the stores.
 *
 * Note: 401 authentication errors are handled at the composable level
 * (storeGraphqlQuery.ts, storeGraphqlMutation.ts) where they trigger token refresh and retry.
 * This handler only deals with genuinely unhandled errors.
 */

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.hook("apollo:error", async (error) => {
    const nuxtApp = useNuxtApp();
    const getToast: typeof useToast = () =>
      nuxtApp.vueApp.config.globalProperties.$toast;
    const toastService = getToast();

    const operation = error.operation.operationName;

    // Check for authentication errors (401, UNAUTHENTICATED)
    const isAuthError = error.graphQLErrors?.some(
      (err) => err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message?.includes('Unauthorized') ||
        err.message?.includes('No authentication detected')
    ) || (error.networkError && 'statusCode' in error.networkError && error.networkError.statusCode === 401);

    if (isAuthError) {
      console.warn('[Apollo] Authentication error detected - session expired');

      const auth = useAuth();
      auth.user = null;
      auth.sessionProvider = null;

      // Pass current route so user returns here after login
      const route = useRoute();
      const config = useRuntimeConfig();
      const authMode = (config.public as any).AUTH_MODE ?? "keycloak";
      if (!auth.loadingSessionProvider && auth.sessionProvider === null) {
        await auth.fetchSessionProvider();
      }
      const wantsKeycloak = auth.sessionProvider
        ? auth.sessionProvider === "keycloak"
        : authMode === "keycloak";
      if (wantsKeycloak) {
        auth.startKeycloakLogin(route.fullPath);
        return;
      }
      if (route.path !== "/login") {
        navigateTo({ path: "/login", query: { returnTo: route.fullPath } });
      }
      return;
    }

    // All other errors: show error toast (these are genuinely unhandled errors)
    let errorMessage = "Es ist ein Fehler aufgetreten";

    console.warn('[Apollo] Unhandled error for operation:', operation, error);

    setTimeout(() => {
      toastService.add({
        severity: "error",
        summary: errorMessage,
        life: 3000,
      });
    }, 3000);
  });
});
